/**
 * Forgot-password + reset-password flow.
 *
 * - /forgot-password → email entry → POST /api/auth/forgot-password → confirmation
 *   (response is intentionally the same whether or not the email exists)
 * - /reset-password?token=... → new password entry (twice) → POST /api/auth/reset-password
 *   → success + redirect to /login
 *
 */
import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { API } from "../context/AuthContext";
import { BUSINESS } from "../config/business";
import { AuthShell, FieldLabel, FormError, PrivateNotice } from "../components/auth/AuthShell";

const Shell = ({ children }) => (
  <AuthShell testid="password-reset-shell">
    {children}
    <PrivateNotice />
    <div className="mt-4 text-center">
      <Link to="/login" data-testid="back-to-login" className="pr-link text-sm">Back to login</Link>
    </div>
  </AuthShell>
);

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      // The endpoint always returns 200; anything else is a network problem.
      setError("Could not reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <div className="mb-4">
        <h2 className="text-lg font-medium">Reset your password</h2>
      </div>
      {sent ? (
        <div data-testid="forgot-password-sent">
          <div className="text-[#14281E] text-sm mb-3 pl-3 border-l border-[#14281E]">Check your inbox.</div>
          <p className="text-[#5E4D40] text-sm leading-relaxed">
            If that email is on file, a reset link has been sent. The link expires in 30 minutes.
            Not seeing it? Check spam, then try again.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <p className="text-[#5E4D40] text-sm">Enter the email you use to sign in. We'll email you a one-time link to set a new password.</p>
          <div>
            <FieldLabel>Email</FieldLabel>
            <input
              data-testid="forgot-password-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pr-input"
              placeholder={BUSINESS.email}
              autoComplete="email"
            />
          </div>
          {error && <FormError testid="forgot-password-error">{error}</FormError>}
          <button
            data-testid="forgot-password-submit"
            type="submit"
            disabled={busy || !email}
            className="pr-btn w-full"
          >
            {busy ? "Sending…" : "Send reset link"}
          </button>
        </form>
      )}
    </Shell>
  );
};

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw1.length < 8) return setError("Password must be at least 8 characters.");
    if (pw1 !== pw2) return setError("Passwords do not match.");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw1 });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not reset password. Request a new link.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <Shell>
        <div data-testid="reset-password-missing-token" className="text-[#5C1A24] text-sm">
          Missing reset token. Open the link from your email — it should contain a token.
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-4">
        <h2 className="text-lg font-medium">Set a new password</h2>
      </div>
      {done ? (
        <div data-testid="reset-password-done">
          <div className="text-[#14281E] text-sm mb-3 pl-3 border-l border-[#14281E]">Password updated. Redirecting to login…</div>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div>
            <FieldLabel>New password</FieldLabel>
            <input
              data-testid="reset-password-new"
              type="password"
              required
              minLength={8}
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              className="pr-input"
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
          </div>
          <div>
            <FieldLabel>Confirm new password</FieldLabel>
            <input
              data-testid="reset-password-confirm"
              type="password"
              required
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="pr-input"
              autoComplete="new-password"
            />
          </div>
          {error && <FormError testid="reset-password-error">{error}</FormError>}
          <button
            data-testid="reset-password-submit"
            type="submit"
            disabled={busy || !pw1 || !pw2}
            className="pr-btn w-full"
          >
            {busy ? "Updating…" : "Update password"}
          </button>
        </form>
      )}
    </Shell>
  );
};
